<template>
  <main>
    <div v-if="isLoading" class="loading-animation">
      <BusyIndicator />
    </div>

    <NotificationBanner
      v-else-if="errorMessage"
      :message="errorMessage"
      is-danger
    />

    <template v-else>
      <h1>{{ topScoringLists.selected?.name }}</h1>

      <div v-if="topScoringLists.selected" class="top-scoring-list-details">
        <div>{{ $t('top-scoring-lists.age-group') }}:</div>
        <div>
          {{
            topScoringLists.selected?.ageGroupName ??
            $t('top-scoring-lists.all-age-groups')
          }}
        </div>

        <div>{{ $t('top-scoring-lists.gender') }}:</div>
        <div>
          {{
            !isNil(topScoringLists.selected?.gender)
              ? formatGender(topScoringLists.selected?.gender, true)
              : $t('top-scoring-lists.all-genders')
          }}
        </div>

        <div>{{ $t('top-scoring-lists.min-game-count') }}:</div>
        <div>
          {{ formatFloat(topScoringLists.selected?.minimumGameCount, 0) }}
        </div>

        <div>{{ $t('top-scoring-lists.last-updated') }}:</div>
        <div>
          {{ formatDate(topScoringLists.selected?.lastUpdatedAt, true) }}
        </div>
      </div>

      <TopScoringList :list="topScoringLists.selected" />
    </template>
  </main>
</template>

<script lang="ts" setup>
import isNil from 'lodash/isNil'
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import TopScoringList from '@/components/widgets/public/TopScoringList.vue'
import { useTopScoringListsStore } from '@/store/public/top-scoring-lists'
import { getErrorMessage } from '@/utils/error-handling'
import { formatDate, formatFloat, formatGender } from '@/utils/format'

const topScoringLists = useTopScoringListsStore()

const errorMessage = ref<string | null>()
const isLoading = ref<boolean>(false)

onMounted(async (): Promise<void> => {
  isLoading.value = true

  const listId = useRoute().params.id

  try {
    await topScoringLists.fetchById(String(listId))
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed fetching top scoring list ID ${listId}: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
})
</script>

<style lang="scss" scoped>
.loading-animation {
  padding: 2em 0;
  font-size: 3em;
  text-align: center;
}

.top-scoring-list-details {
  display: grid;
  grid-template-columns: repeat(2, min-content);
  gap: 0.25em 1em;

  div {
    white-space: nowrap;
  }
}
</style>
